import { useCallback, useMemo, useRef, useState } from 'react';
import {
  PrimaryNavigationMenu,
  PrimaryNavigationMenuItem,
  PrimaryNavigationMenuSearch,
  PrimaryNavigationMenuShowMoreItem,
} from 'components/ui';
import { useSearchableLimitedOptions } from 'components/ui/hooks';
import { useZoom } from 'context/ZoomContext';
import { toString } from 'utilities';
import { WATERFALL_SEARCHABLE_CELL_SELECT_VISIBLE_ITEM_LIMIT } from './WaterfallSearchableCellSelect.constants';
import { WaterfallSearchableCellSelectProps } from './WaterfallSearchableCellSelect.props';
import {
  WaterfallSearchableCellSelectIcon,
  WaterfallSearchableCellSelectTrigger,
  WaterfallSearchableCellSelectValue,
} from './WaterfallSearchableCellSelect.styles';

const WaterfallSearchableCellSelect = ({
  cell,
  emptyMessage,
  id,
  itemLabelPlural,
  itemLabelSingular,
  onCommit,
  onRevert,
  options,
  searchPlaceholder,
}: WaterfallSearchableCellSelectProps) => {
  const { zoomLevel } = useZoom();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const selectedValue = toString(cell.value);
  const selectedLabel = useMemo(
    () => options.find(option => toString(option.value) === selectedValue)?.label ?? '',
    [options, selectedValue]
  );
  const getSearchText = useCallback((option: (typeof options)[number]) => option.label, []);
  const {
    canShowFewerOptions,
    hasMoreOptions,
    hiddenOptionCount,
    resetSearchableOptions,
    searchValue,
    setSearchValue,
    setShowAllOptions,
    visibleOptions,
  } = useSearchableLimitedOptions({
    getSearchText,
    options,
    visibleLimit: WATERFALL_SEARCHABLE_CELL_SELECT_VISIBLE_ITEM_LIMIT,
  });

  const handleTriggerRef = useCallback((node: HTMLButtonElement | null) => {
    setAnchorEl(node);
  }, []);

  const handleClose = useCallback(() => {
    resetSearchableOptions();
    onRevert?.();
  }, [onRevert, resetSearchableOptions]);

  const handleSelect = useCallback(
    (value: string | number | null) => {
      resetSearchableOptions();
      onCommit?.(value == null ? null : toString(value));
    },
    [onCommit, resetSearchableOptions]
  );

  return (
    <>
      <WaterfallSearchableCellSelectTrigger
        aria-expanded={Boolean(anchorEl)}
        aria-haspopup="menu"
        data-testid={id}
        ref={handleTriggerRef}>
        <WaterfallSearchableCellSelectValue>{selectedLabel}</WaterfallSearchableCellSelectValue>
        <WaterfallSearchableCellSelectIcon />
      </WaterfallSearchableCellSelectTrigger>
      <PrimaryNavigationMenu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
        disableAutoFocusItem
        menuMaxWidth={320}
        menuMinWidth={200}
        menuWidth="content"
        onClose={handleClose}
        onMouseDown={event => event.stopPropagation()}
        open={Boolean(anchorEl)}
        PaperProps={{ sx: { marginLeft: '-1px', marginTop: '-1px' } }}
        scrollHintItemLabelPlural={itemLabelPlural}
        scrollHintItemLabelSingular={itemLabelSingular}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        zoomLevel={zoomLevel}
        TransitionProps={{ onEntered: () => searchInputRef.current?.focus() }}
        MenuListProps={{ disableListWrap: true }}>
        <PrimaryNavigationMenuSearch
          clearAriaLabel={`Clear ${itemLabelSingular} search`}
          fullWidth
          inputProps={{ 'data-testid': `${id}-search` }}
          inputRef={searchInputRef}
          onChange={event => setSearchValue(event.target.value)}
          onClear={() => setSearchValue('')}
          placeholder={searchPlaceholder}
          value={searchValue}
        />
        {visibleOptions.map(option => {
          const optionValue = toString(option.value);

          return (
            <PrimaryNavigationMenuItem
              data-testid={`${id}-option-${optionValue}`}
              isActive={optionValue === selectedValue}
              key={optionValue}
              onClick={() => handleSelect(option.value)}>
              {option.label}
            </PrimaryNavigationMenuItem>
          );
        })}
        {visibleOptions.length === 0 && (
          <PrimaryNavigationMenuItem disabled tone="muted">
            {emptyMessage}
          </PrimaryNavigationMenuItem>
        )}
        {hasMoreOptions && (
          <PrimaryNavigationMenuShowMoreItem
            count={hiddenOptionCount}
            itemLabelPlural={itemLabelPlural}
            itemLabelSingular={itemLabelSingular}
            onClick={() => setShowAllOptions(true)}
            variant="more"
          />
        )}
        {canShowFewerOptions && (
          <PrimaryNavigationMenuShowMoreItem
            itemLabelPlural={itemLabelPlural}
            itemLabelSingular={itemLabelSingular}
            onClick={() => setShowAllOptions(false)}
            variant="fewer"
          />
        )}
      </PrimaryNavigationMenu>
    </>
  );
};

export default WaterfallSearchableCellSelect;
